/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量打标 handlers hook (apply / unbind)
 *
 * 抽离自 photov2/PhotosList.tsx + ai-photo-king/PhotosList.tsx (review #4):
 * 两个 PhotosList 的 applyTagsHandler / unbindTagsHandler 几乎完全一样 (~190 行 × 2),
 * 仅 fromPage 不同, 提取到共用 hook 消除重复。
 *
 * 返回的 handlers 供 BatchTagModeProvider 的 applyTagsHandler / unbindTagsHandler 注入。
 *
 * 设计要点:
 * - exclusive 兜底: 后端未生效时先 unbind team 所有 active label 再 bind 新的 (review #3)
 * - 乐观更新: bind 成功立即 dispatch appendLabelsToPhotos / unbind 立即 dispatch updateLabelsAcrossPhotos
 * - 失败容错: 乐观更新失败不阻断主流程, 只是少了立即刷新效果
 */
import { useCallback } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { buildParamsWithAuthPolicy } from '../../../actions/photo'
import {
  appendLabelsToPhotos,
  updateLabelsAcrossPhotos
} from '../../../slices/photosByContentSlice'
import { batchBindLabels, batchUnbindLabels } from '../api'
import { trackLabelBindSuccess } from '../../components/PhotoFilter/utils/sensorUtils'
import type {
  ApplyTagsHandler,
  UnbindTagsHandler
} from '../../components/BatchTagMode'

export interface UseBatchTagHandlersParams {
  /** 当前查询条件 (来自 selectSearchFilter / selectPreciseSearch) */
  queryFilter: any
  groupId: string
  /** 当前已加载的 photos 数组 (虚拟列表 visible 部分) */
  photos: any[]
  /** checkAll 模式 */
  checkAll: boolean
  /** checkAll=true 时排除的 photoIDs */
  unCheckedPhotos: Set<number>
  /** checkAll=false 时勾选的 photoIDs */
  checkedPhotos: Set<number>
  /** 用于构造 filter.include (checkAll=false 模式) */
  include: string[]
  /** 用于构造 filter.exclude (checkAll=true 模式) */
  exclude: string[]
  /** 团队全部 active 标签 (用于 exclusive 兜底 + 乐观更新反查 color) */
  teamAllTagsForBatch: any[]
  /** 用于构造 fetch params */
  buildFetchPhotosParamsWithOptions: (opts: any) => any
  /** 来源页 (埋点 / 文案) */
  fromPage: 'photov2' | 'ai_photo_king' | 'photo'
}

export interface UseBatchTagHandlersReturn {
  applyTagsHandler: ApplyTagsHandler
  unbindTagsHandler: UnbindTagsHandler
}

export function useBatchTagHandlers(
  params: UseBatchTagHandlersParams
): UseBatchTagHandlersReturn {
  const {
    queryFilter,
    groupId,
    photos,
    checkAll,
    unCheckedPhotos,
    checkedPhotos,
    include,
    exclude,
    teamAllTagsForBatch,
    buildFetchPhotosParamsWithOptions
    // fromPage 字段保留在 params interface 是给未来扩展用 (v3 埋点细分页面来源);
    // 当前 trackLabelBindSuccess 老枚举只有 '全部照片页批量打标签' / '照片预览页打标签',
    // 两个页面统一上报第一个值 → 这里不需要 destructure
  } = params

  const store = useStore()
  const dispatch = useDispatch()

  const applyTagsHandler: ApplyTagsHandler = useCallback(
    async ({ labelIDs, labelNames }) => {
      if (!queryFilter || !groupId) {
        throw new Error('缺少筛选条件或团队 ID')
      }
      const rawParams = buildFetchPhotosParamsWithOptions({
        queryFilter,
        groupID: groupId,
        lastPageCond: '',
        isPreciseSearch: false
      })
      const baseParams = buildParamsWithAuthPolicy(store.getState(), rawParams)
      if (checkAll) {
        baseParams.filter.exclude = exclude
      } else {
        baseParams.filter.include = include
      }

      // 兜底:后端 exclusive 没生效时 (deploy 时差) 防止变 append。
      // labelIDs 传 team 所有 active label IDs:「1 张 1 标签」语义下每张实际只删 1 个旧的。
      // 后端 exclusive 部署后这步变 no-op (rel 已被清空), 可安全保留。
      // TODO(后端 exclusive 全量灰度后可删)
      const allTeamLabelIDsForFallback = teamAllTagsForBatch
        .map((t: any) => String(t.id))
        .filter(Boolean)
      if (allTeamLabelIDsForFallback.length > 0) {
        try {
          await batchUnbindLabels({
            groupID: groupId,
            filter: baseParams.filter,
            labelIDs: allTeamLabelIDsForFallback,
            isPreciseSearch: false
          })
        } catch (e) {
          console.warn('[batchUnbind fallback] failed, continue', e)
        }
      }
      const result = await batchBindLabels({
        groupID: groupId,
        filter: baseParams.filter,
        labelIDs,
        isPreciseSearch: false,
        // 「1 张照片 1 个标签」约束: 后端先清空命中 photos 的旧标签再打新的.
        // TagPicker 在 BatchTagBar 那边已经强制 singleSelect=true, labelIDs 永远只有 1 个.
        exclusive: true
      })
      // 乐观更新 redux 里被选中 photos 的 labels —
      // 不等下次 fetch,bind 成功就立即把标签 append 到本地 state,UI 立刻刷新
      try {
        const affectedSeqs: number[] = checkAll
          ? photos
              .filter((p: any) => !unCheckedPhotos.has(p.seq))
              .map((p: any) => p.seq)
          : photos
              .filter((p: any) => checkedPhotos.has(p.seq))
              .map((p: any) => p.seq)
        // 用 teamAllTags 反查 color/name,接口没返完整 tag 信息
        const newLabels = labelIDs.map((id, i) => {
          const tag = teamAllTagsForBatch.find(
            (t: any) => String(t.id) === String(id)
          )
          return tag
            ? {
                id: String(tag.id),
                name: tag.name,
                color: tag.color || '#2979FF'
              }
            : {
                id: String(id),
                name: labelNames[i] || '',
                color: '#2979FF'
              }
        })
        if (affectedSeqs.length && newLabels.length) {
          // exclusive 跟后端 bind 保持一致, 让 redux 覆盖旧 labels 不要 append
          dispatch(
            appendLabelsToPhotos({ affectedSeqs, newLabels, exclusive: true })
          )
        }
      } catch (_) {
        // 乐观更新失败不影响打标主流程,大不了刷新页面才能看到
      }
      try {
        // fromPlace 老埋点上报 — photov2 / ai_photo_king 都统一上报"全部照片页批量打标签"
        // (老枚举只有两个: '全部照片页批量打标签' | '照片预览页打标签')
        // v3 新埋点的细分页面来源走 fromPage 字段, 这里走老的 sensorUtils 不动
        trackLabelBindSuccess({
          photoNum: result.boundPhotoCount,
          labelName: labelNames.join('|'),
          fromPlace: '全部照片页批量打标签'
        })
      } catch (_) {
        // 埋点失败不阻塞主流程
      }
      return {
        boundPhotoCount: result.boundPhotoCount,
        boundLabelCount: result.boundLabelCount
      }
    },
    [
      queryFilter,
      groupId,
      checkAll,
      exclude,
      include,
      store,
      photos,
      checkedPhotos,
      unCheckedPhotos,
      teamAllTagsForBatch,
      dispatch,
      buildFetchPhotosParamsWithOptions
    ]
  )

  // 批量移除标签 handler — 跟 applyTagsHandler 镜像同套 filter 构造.
  const unbindTagsHandler: UnbindTagsHandler = useCallback(
    async ({ labelIDs }) => {
      if (!queryFilter || !groupId) {
        throw new Error('缺少筛选条件或团队 ID')
      }
      const rawParams = buildFetchPhotosParamsWithOptions({
        queryFilter,
        groupID: groupId,
        lastPageCond: '',
        isPreciseSearch: false
      })
      const baseParams = buildParamsWithAuthPolicy(store.getState(), rawParams)
      if (checkAll) {
        baseParams.filter.exclude = exclude
      } else {
        baseParams.filter.include = include
      }
      const result = await batchUnbindLabels({
        groupID: groupId,
        filter: baseParams.filter,
        labelIDs,
        isPreciseSearch: false
      })
      // 乐观更新: 把这些 label 从被选中 photos 的 labels 移除 (限定 affectedSeqs 避免误伤未选中)
      try {
        const affectedSeqs: number[] = checkAll
          ? photos
              .filter((p: any) => !unCheckedPhotos.has(p.seq))
              .map((p: any) => p.seq)
          : photos
              .filter((p: any) => checkedPhotos.has(p.seq))
              .map((p: any) => p.seq)
        if (affectedSeqs.length && labelIDs.length) {
          dispatch(
            updateLabelsAcrossPhotos({
              deletedIds: labelIDs,
              affectedSeqs
            })
          )
        }
      } catch (_) {
        // 乐观更新失败不影响主流程, 大不了刷新页面才能看到
      }
      return {
        removedRelCount: result.removedRelCount,
        affectedPhotoCount: result.affectedPhotoCount,
        affectedLabelCount: result.affectedLabelCount
      }
    },
    [
      queryFilter,
      groupId,
      checkAll,
      exclude,
      include,
      store,
      photos,
      checkedPhotos,
      unCheckedPhotos,
      dispatch,
      buildFetchPhotosParamsWithOptions
    ]
  )

  return { applyTagsHandler, unbindTagsHandler }
}
